import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  Grid,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../services/api';

/** Colour thresholds shared by the rate cells and the summary tiles. */
const rateColor = (pct) => {
  if (pct >= 70) return '#2e7d32';   // green
  if (pct >= 30) return '#ed6c02';   // amber
  return '#d32f2f';                  // red
};

/** A labelled headline number. */
const StatTile = ({ label, value, suffix = '', color, hint }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Box>
      <Typography variant="h4" sx={{ mt: 0.5, color: color || 'text.primary' }}>
        {value}
        {suffix && (
          <Typography component="span" variant="h6" sx={{ color: 'inherit' }}>
            {suffix}
          </Typography>
        )}
      </Typography>
    </CardContent>
  </Card>
);

/** A percentage cell rendered as a number plus a proportional bar. */
const RateCell = ({ pct, muted }) => (
  <TableCell align="right" sx={{ minWidth: 110 }}>
    {muted ? (
      <Typography variant="body2" color="text.disabled">—</Typography>
    ) : (
      <Box>
        <Typography variant="body2" sx={{ color: rateColor(pct), fontWeight: 600 }}>
          {pct.toFixed(0)}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          sx={{
            height: 4,
            borderRadius: 2,
            mt: 0.5,
            backgroundColor: 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': { backgroundColor: rateColor(pct) },
          }}
        />
      </Box>
    )}
  </TableCell>
);

/**
 * Test-coverage matrix — the inventory-wide view of how much of each module is
 * covered, how much of that coverage is automated, and how much is traced to a
 * JIRA story. Distinct from Reports, which reports one release's execution
 * outcome.
 */
const CoverageMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/reports/coverage', {
        params: { include_all_statuses: includeAll },
      });
      setData(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || 'Failed to load coverage data'
      );
    } finally {
      setLoading(false);
    }
  }, [includeAll]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  const totals = data?.totals;
  // Covered modules first (most gaps are the actionable part, but leaders want
  // the real numbers up top), then the empty ones.
  const rows = (data?.modules || []).slice().sort((a, b) => {
    if (a.has_no_tests !== b.has_no_tests) return a.has_no_tests ? 1 : -1;
    return b.total - a.total;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 1,
          columnGap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h4" sx={{ flexGrow: 1, whiteSpace: 'nowrap' }}>
          Test Coverage Matrix
        </Typography>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Switch
              size="small"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Include draft / broken / deprecated
            </Typography>
          }
        />
        <IconButton onClick={fetchCoverage} disabled={loading} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Coverage across the whole test inventory, by module. By default only
        vetted (active) test cases are counted, so work in progress never
        inflates the numbers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {totals && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Automation rate"
                value={totals.automation_rate.toFixed(0)}
                suffix="%"
                color={rateColor(totals.automation_rate)}
                hint={`${totals.automated} automated of ${totals.counted} counted test cases`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Requirement traceability"
                value={totals.traceability_rate.toFixed(0)}
                suffix="%"
                color={rateColor(totals.traceability_rate)}
                hint="Share of counted test cases linked to a JIRA story"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Test cases counted"
                value={totals.counted}
                hint={
                  totals.excluded > 0
                    ? `${totals.excluded} excluded (draft / broken / deprecated) of ${totals.inventory} total`
                    : `${totals.inventory} in the inventory`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Modules with no tests"
                value={totals.modules_with_no_tests}
                color={totals.modules_with_no_tests > 0 ? '#d32f2f' : '#2e7d32'}
                hint="Modules that have no test cases at all — the clearest coverage gaps"
              />
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Automated</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Manual</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Automation</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Traceability</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Excluded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((m) => (
                  <TableRow
                    key={m.module_id ?? m.module_name}
                    hover
                    sx={m.has_no_tests ? { backgroundColor: 'rgba(211,47,47,0.04)' } : undefined}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {m.module_name}
                        {m.has_no_tests && (
                          <Chip label="no tests" size="small" color="error" variant="outlined" />
                        )}
                        {m.module_id === null && (
                          <Tooltip title="Test cases with no module assigned — they are shown so the totals always reconcile">
                            <Chip label="unassigned" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{m.automated}</TableCell>
                    <TableCell align="right">{m.manual}</TableCell>
                    <TableCell align="right">{m.total}</TableCell>
                    <RateCell pct={m.automation_rate} muted={m.has_no_tests} />
                    <RateCell pct={m.traceability_rate} muted={m.has_no_tests} />
                    <TableCell align="right">
                      {m.excluded > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {m.excluded}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.15)' } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{totals.automated}</TableCell>
                  <TableCell align="right">{totals.manual}</TableCell>
                  <TableCell align="right">{totals.counted}</TableCell>
                  <RateCell pct={totals.automation_rate} />
                  <RateCell pct={totals.traceability_rate} />
                  <TableCell align="right">{totals.excluded || '—'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Counting statuses:{' '}
            {Array.isArray(data.counted_statuses)
              ? data.counted_statuses.join(', ')
              : data.counted_statuses}
            . Automation = automated ÷ total. Traceability = linked to a JIRA story ÷ total.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default CoverageMatrix;
